/**
 * WlTenantSessionsTab.tsx
 * ----------------------------------------------------------------------------
 * Aba "Sessões" do modal WlTenantDetailsModal. Mostra o histórico de sessões
 * de suporte (operadores WL) executadas SOBRE este tenant específico.
 *
 * Consome wl_list_support_sessions com filtro de tenant fixo.
 *
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { KeyRound, Activity, CheckCircle2, AlertTriangle } from "lucide-react";
import { SupportSessionsTable } from "./SupportSessionsTable";
import { SupportSessionDetailsDrawer } from "./SupportSessionDetailsDrawer";
import {
  useWlSupportSessions,
  type SupportSessionRow,
} from "@/hooks/wl/useSupportSessionsAudit";

interface WlTenantSessionsTabProps {
  tenantId: string | null;
}

interface MiniKpiProps {
  icon: typeof KeyRound;
  label: string;
  value: number | string;
  tone?: "default" | "success" | "warning";
}

function MiniKpi({ icon: Icon, label, value, tone = "default" }: MiniKpiProps) {
  const toneClass =
    tone === "success"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : tone === "warning"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : "bg-muted text-foreground";
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold leading-tight">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function WlTenantSessionsTab({ tenantId }: WlTenantSessionsTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const q = useWlSupportSessions(
    { tenantId, limit: 100, offset: 0 },
    !!tenantId,
  );

  const rows = q.data ?? [];

  // KPIs derivados da página atual (até 100 sessões)
  const kpis = useMemo(() => {
    const total = rows[0]?.total_count ?? rows.length;
    const active = rows.filter((r) => r.status === "active").length;
    const ended = rows.filter((r) => r.status === "ended").length;
    const expired = rows.filter((r) => r.status === "expired").length;
    return { total, active, ended, expired };
  }, [rows]);

  const handleRowClick = (row: SupportSessionRow) => {
    setSelectedId(row.session_id);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi icon={KeyRound}      label="Total"      value={kpis.total} />
        <MiniKpi icon={Activity}      label="Ativas"     value={kpis.active}  tone="success" />
        <MiniKpi icon={CheckCircle2}  label="Encerradas" value={kpis.ended} />
        <MiniKpi icon={AlertTriangle} label="Expiradas"  value={kpis.expired} tone="warning" />
      </div>

      {/* Tabela */}
      <SupportSessionsTable
        rows={rows}
        loading={q.isLoading}
        showTenantColumn={false}
        onRowClick={handleRowClick}
        emptyMessage="Nenhum operador acessou este tenant como suporte ainda."
      />

      <SupportSessionDetailsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        sessionId={selectedId}
        scope="wl"
      />
    </div>
  );
}
